import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";

export interface Outlet {
  outlet_id: string;
  business_id: string;
  outlet_name: string;
  address: string | null;
  is_active: boolean;
  created_at: string;
  description: string | null;
  phone_number: string | null;
  business_email: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  avatar_url: string | null;
  slug: string | null;
}

interface OutletContextType {
  outlets: Outlet[];
  activeOutlet: Outlet | null;
  /** True only when the owner explicitly selected the aggregate, read-only view. */
  isAllOutlets: boolean;
  loading: boolean;
  setActiveOutlet: (outlet: Outlet | null) => void;
  refreshOutlets: () => Promise<void>;
  createDefaultOutlet: (businessId: string, name: string) => Promise<void>;
}

const OutletContext = createContext<OutletContextType | undefined>(undefined);
const ALL_OUTLETS_STORAGE_VALUE = "__all__";

export const OutletProvider = ({ children }: { children: ReactNode }) => {
  const { businessId, user, isStaff, staffOutletId } = useAuth();
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [activeOutlet, setActiveOutletState] = useState<Outlet | null>(null);
  const [loading, setLoading] = useState(true);
  const isAllOutlets =
    !loading &&
    activeOutlet === null &&
    outlets.filter((outlet) => outlet.is_active !== false).length > 1;

  const fetchOutlets = async () => {
    if (!businessId) {
      setOutlets([]);
      setActiveOutletState(null);
      setLoading(false);
      return;
    }

    // Staff: fetch all outlets they are assigned to for this business
    if (isStaff && user) {
      try {
        const { data: staffRecords, error: staffError } = await supabase
          .from("staff_members")
          .select("outlet_id")
          .eq("user_id", user.id)
          .eq("business_id", businessId)
          .in("status", ["active", "pending"]);

        if (staffError) throw staffError;

        if (staffRecords && staffRecords.length > 0) {
          const outletIds = staffRecords.map(r => r.outlet_id).filter(Boolean);
          
          if (outletIds.length > 0) {
            const { data, error } = await supabase
              .from("outlets")
              .select("*")
              .in("outlet_id", outletIds)
              .order("created_at", { ascending: true }) as { data: Outlet[] | null; error: any };

            if (!error && data) {
              setOutlets(data);
              
              const savedOutletId = localStorage.getItem("dibs_active_outlet");
              const activeOutlets = data.filter(o => o.is_active !== false);
              const currentOutletIds = data.map(o => o.outlet_id);
              
              // Check if any previously known outlets were removed
              const knownOutletsStr = localStorage.getItem("dibs_known_outlets");
              if (knownOutletsStr) {
                try {
                  const knownOutlets = JSON.parse(knownOutletsStr);
                  const removedOutlets = knownOutlets.filter((id: string) => !currentOutletIds.includes(id));
                  if (removedOutlets.length > 0) {
                    toast({
                      title: "Outlet Access Changed",
                      description: "Your access to one or more outlets has been revoked by the administrator.",
                      variant: "default",
                    });
                  }
                } catch (e) {
                  // ignore parse errors
                }
              }
              // Update known outlets
              localStorage.setItem("dibs_known_outlets", JSON.stringify(currentOutletIds));
              
              if (activeOutlets.length > 0) {
                const matchingOutlet = activeOutlets.find((o) => o.outlet_id === savedOutletId);
                if (savedOutletId === ALL_OUTLETS_STORAGE_VALUE && activeOutlets.length > 1) {
                  setActiveOutletState(null);
                } else if (matchingOutlet) {
                  setActiveOutletState(matchingOutlet);
                } else {
                  setActiveOutletState(activeOutlets[0]);
                  localStorage.setItem("dibs_active_outlet", activeOutlets[0].outlet_id);
                  if (savedOutletId && savedOutletId !== ALL_OUTLETS_STORAGE_VALUE) {
                     toast({
                       title: "Active Outlet Changed",
                       description: `You have been switched to ${activeOutlets[0].outlet_name} as your previous outlet is no longer available.`,
                       variant: "default"
                     });
                  }
                }
              } else {
                setActiveOutletState(data[0]);
                localStorage.removeItem("dibs_active_outlet");
              }
            } else {
              setOutlets([]);
              setActiveOutletState(null);
            }
          }
        } else {
          setOutlets([]);
          setActiveOutletState(null);
        }
      } catch (err) {
        console.error("Failed to fetch staff outlets:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const { data, error } = (await supabase
        .from("outlets")
        .select("*")
        .eq("business_id", businessId)
        .order("created_at", { ascending: true })) as { data: Outlet[] | null; error: any };

      if (error) throw error;

      if (data && data.length > 0) {
        // Auto-assign and persist slugs for outlets that are missing them
        for (let i = 0; i < data.length; i++) {
          if (!data[i].slug) {
            const baseName = data[i].outlet_name
              ? data[i].outlet_name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
              : 'outlet';
            const autoSlug = `${baseName}-${Math.random().toString(36).substring(2, 6)}`;
            data[i].slug = autoSlug;
            
            // Persist the generated slug
            await supabase.from("outlets").update({ slug: autoSlug }).eq("outlet_id", data[i].outlet_id);
            
            // If main outlet, sync fallback business slug
            if (i === 0) {
              await supabase.from("businesses").update({ slug: autoSlug }).eq("business_id", businessId);
            }
          }
        }
      }

      setOutlets(data || []);

      if (data && data.length > 0) {
        // Check local storage for previously selected outlet
        const savedOutletId = localStorage.getItem("dibs_active_outlet");
        const activeOutlets = data.filter(o => o.is_active !== false);
        
        if (activeOutlets.length > 0) {
          const matchingOutlet = activeOutlets.find((o) => o.outlet_id === savedOutletId);
          if (savedOutletId === ALL_OUTLETS_STORAGE_VALUE && activeOutlets.length > 1) {
            setActiveOutletState(null);
          } else if (matchingOutlet) {
            setActiveOutletState(matchingOutlet);
            localStorage.setItem("dibs_active_outlet", matchingOutlet.outlet_id);
          } else {
            setActiveOutletState(activeOutlets[0]);
            localStorage.setItem("dibs_active_outlet", activeOutlets[0].outlet_id);
          }
        } else {
          // All outlets are deactivated, fallback to the first one so the app doesn't crash
          setActiveOutletState(data[0]);
          localStorage.removeItem("dibs_active_outlet");
        }
      } else {
        // Leave to webhook to create the main outlet
        setActiveOutletState(null);
        localStorage.removeItem("dibs_active_outlet");
      }
    } catch (err) {
      console.error("Failed to fetch outlets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutlets();
  }, [businessId]);

  const setActiveOutlet = (outlet: Outlet | null) => {
    setActiveOutletState(outlet);
    if (outlet) {
      localStorage.setItem("dibs_active_outlet", outlet.outlet_id);
    } else {
      localStorage.setItem("dibs_active_outlet", ALL_OUTLETS_STORAGE_VALUE);
    }
  };

  const createDefaultOutlet = async (busId: string, name: string) => {
    // Fetch business defaults to copy into the new outlet
    const { data: bizData } = await supabase
      .from("businesses")
      .select("description, phone_number, business_email, instagram_url, facebook_url, twitter_url, avatar_url")
      .eq("business_id", busId)
      .single();

    const baseName = name
      ? name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
      : 'outlet';
    const autoSlug = `${baseName}-${Math.random().toString(36).substring(2, 6)}`;

    const { data: newOutlet, error } = (await supabase
      .from("outlets")
      .insert({
        business_id: busId,
        outlet_name: name,
        description: bizData?.description || null,
        phone_number: bizData?.phone_number || null,
        business_email: bizData?.business_email || null,
        instagram_url: bizData?.instagram_url || null,
        facebook_url: bizData?.facebook_url || null,
        twitter_url: bizData?.twitter_url || null,
        avatar_url: bizData?.avatar_url || null,
        slug: autoSlug,
      })
      .select("*")
      .single()) as { data: Outlet | null; error: any };

    if (!error && newOutlet) {
      await fetchOutlets();
    }
  };

  return (
    <OutletContext.Provider
      value={{
        outlets,
        activeOutlet,
        isAllOutlets,
        loading,
        setActiveOutlet,
        refreshOutlets: fetchOutlets,
        createDefaultOutlet,
      }}
    >
      {children}
    </OutletContext.Provider>
  );
};

export const useOutlet = () => {
  const context = useContext(OutletContext);
  if (context === undefined) {
    throw new Error("useOutlet must be used within an OutletProvider");
  }
  return context;
};
